import React from 'react';
import { Box, ButtonV2, FormControl, InputV2, LinkV2, SpacingStack, TextV2, TooltipV2 } from '@withjoy/joykit';
import { useTrackingSectionController } from './TrackingSection.controller';
import { ReactComponent as HelpIcon } from '@assets/icons/help-info-icon.svg';
import { animated } from 'react-spring';
import type { RegistryFulfillmentFragment } from '@graphql/fragments/registry.graphql.generated';

export type TrackingSectionProps = {
  orderId: string;
  fulfillment: Maybe<RegistryFulfillmentFragment>;
};

export const TrackingSection = ({ orderId, fulfillment }: TrackingSectionProps) => {
  const { isTrackingOpen, springProps, trackFormik, openTrackinSection, loading, translations } = useTrackingSectionController({ orderId, fulfillment });
  const { addTrackingCta, addTrackingTooltip, orderNumberLabel, saveCta, testLink, trackingNumberLabel, updateCta } = translations;

  return (
    <>
      {!isTrackingOpen ? (
        <Box
          textAlign="center"
          position="relative"
          paddingBottom="3"
        >
          {fulfillment?.orderNumber ? (
            <ButtonV2
              intent="productive"
              variant="outline"
              size="md"
              onClick={openTrackinSection}
            >
              {updateCta}
            </ButtonV2>
          ) : (
            <TooltipV2
              placement="topEnd"
              content={<TextV2 typographyVariant="label2">{addTrackingTooltip}</TextV2>}
            >
              <ButtonV2
                intent="productive"
                variant="outline"
                endIcon={<HelpIcon />}
                size="md"
                onClick={openTrackinSection}
              >
                {addTrackingCta}
              </ButtonV2>
            </TooltipV2>
          )}
        </Box>
      ) : (
        <Box
          as={animated.div}
          padding={5}
          style={springProps}
        >
          <form onSubmit={trackFormik.handleSubmit}>
            <SpacingStack spacing={6}>
              <FormControl
                error={
                  <TextV2
                    typographyVariant="label2"
                    marginTop={2}
                    color={'inputBorderError'}
                    hidden={!trackFormik.getFieldMeta('orderNumber').error}
                  >
                    {trackFormik.getFieldMeta('orderNumber').error}
                  </TextV2>
                }
                label={
                  <TextV2
                    typographyVariant="label3"
                    as="label"
                  >
                    {orderNumberLabel}
                  </TextV2>
                }
                isInvalid={!!trackFormik.getFieldMeta('orderNumber').error}
              >
                <InputV2 {...trackFormik.getFieldProps('orderNumber')} />
              </FormControl>
              <FormControl
                label={
                  <TextV2
                    typographyVariant="label3"
                    as="label"
                  >
                    {trackingNumberLabel}
                  </TextV2>
                }
                caption={
                  trackFormik.getFieldMeta('trackingNumber').value === '' ? (
                    <TextV2
                      typographyVariant="label2"
                      marginTop={2}
                      color="mono10"
                    >
                      {testLink}
                    </TextV2>
                  ) : (
                    <LinkV2
                      target="__blank"
                      typographyVariant="label2"
                      marginTop={2}
                      href={`https://www.google.com/search?q=${trackFormik.getFieldMeta('trackingNumber').value}`}
                      textDecoration="none"
                    >
                      {testLink}
                    </LinkV2>
                  )
                }
                isInvalid={!!trackFormik.getFieldMeta('trackingNumber').error}
                isDisabled={trackFormik.getFieldMeta('orderNumber').value === ''}
              >
                <InputV2 {...trackFormik.getFieldProps('trackingNumber')} />
              </FormControl>
              <ButtonV2
                intent="productive"
                variant="outline"
                type="submit"
                disabled={loading}
                loading={loading}
              >
                {saveCta}
              </ButtonV2>
            </SpacingStack>
          </form>
        </Box>
      )}
    </>
  );
};
